import React, { useState, useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import RemoteLayout from "./layout";
import { Conference } from "../../components/videoCalling";
import { WorkshopContext } from "../../contexts/workshop.context";
import { defaultRoles } from "../../util/workshop.util";
import { getNextRoute, getPreviousRoute } from "../../util/general.util";

const TASK_ID = 9;

const Task9b = ({ summary = false }) => {
  const [wordOptions, setWordOptions] = useState([]);
  const [result, setResult] = useState([]); // { word, score }
  const [finalModSelections, setFinalModSelections] = useState({});
  const [started, setStarted] = useState(false); // Timer
  const [finished, setFinished] = useState(false);

  const {
    socket,
    user: { id, role } = {},
    themeConfig: { primary } = {},
    workshopDetails: { platform } = {}
  } = useContext(WorkshopContext);

  const { t } = useTranslation();

  useEffect(() => {
    try {
      if (wordOptions.length !== 0) return;

      const { attitudes } = t("mtParticipantsActivityNine", { returnObjects: true });

      setWordOptions(attitudes);
    } catch (error) {
      console.log(error);
    }
  }, [wordOptions]);

  const selectModWord = (index) => {
    if (role == defaultRoles.participant || finished) return;

    socket.emit("task9ModSelection", index);
  };

  useEffect(() => {
    if (!id && !summary) return;

    socket.emit("task9");

    socket.on("task9", ({ wordScores, modSelections }) => {
      if (Object.keys(wordScores).length !== 0) {
        setStarted(true);

        setResult(
          Object.entries(wordScores)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 8)
        );
      }

      setFinalModSelections(modSelections);
    });

    socket.emit("groupTaskStatus", TASK_ID, (status) => {
      if (status?.["all"]?.finished) {
        setFinished(true);
      }
    });

    socket.on("groupTaskStatus", (status) => {
      if (status?.[TASK_ID]?.["all"]?.finished) {
        setFinished(true);
      } else {
        setFinished(false);
      }
    });
  }, [id]);

  const handleNext = () => {
    // Count only true values in finalModSelections
    const selectedTrueCount = Object.values(finalModSelections).filter(Boolean).length;
    const totalCount = Object.values(finalModSelections).length;

    if (totalCount < 4 || selectedTrueCount < 4) {
      toast.info(t("task9.b.selectAtLeastFour") || "Please select at least 4 words.");
      return;
    }
    const nextRoute = getNextRoute("/task-9/b", platform);
    socket.emit("navigate", nextRoute);
  };

  const handleBack = () => {
    const previousRoute = getPreviousRoute(`/task-9/b`, platform);
    socket.emit("navigate", previousRoute);
  };

  const showBackButton = role !== defaultRoles.participant;

  return (
    <RemoteLayout
      buttonText={t("buttonNext") + " →"}
      onButtonClick={!finished && role !== defaultRoles.participant ? handleNext : null}
      showParticipants={true}
      onBackClick={showBackButton && handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-white text-2xl md:text-4xl font-bold flex items-center justify-center gap-2 text-white-100">
          <span className="text-yellow-500">🏆</span> {t("task9.b.title")}
        </h1>
        <p
          className="mt-4 max-w-2xl mx-auto text-white-100 text-lg"
          dangerouslySetInnerHTML={{
            __html:
              role === defaultRoles.participant
                ? t("task9.b.subheading")
                : role === defaultRoles.moderator
                  ? t("task9.b.subheadingMod")
                  : t("task9.b.subheadingModPart")
          }}
        />
      </div>

      {console.log("finalModSelections", finalModSelections)}

      {/* Main Content */}
      <div className="flex flex-col md:flex-row gap-8 justify-center items-center w-full mx-auto">
        <div className="bg-orange-50 text-black p-6 rounded-2xl shadow-2xl w-full md:w-4/12">
          <h2 className="text-orange-default font-bold text-xl mb-4 text-center">
            {t("task9.b.card.title")}
          </h2>
          <ul className="list-inside">
            <li
              className="py-1"
              dangerouslySetInnerHTML={{ __html: t("task9.b.card.list.item1") }}
            />
            <li
              className="py-1"
              dangerouslySetInnerHTML={{ __html: t("task9.b.card.list.item2") }}
            />
          </ul>
        </div>
        <div className="flex flex-col gap-4 w-full md:w-8/12">
          <div className="flex flex-wrap gap-2 items-center justify-start content-center my-4">
            {result.map((w, index) => {
              const [wordIndex, wordScore] = w;
              const word = wordOptions[wordIndex];
              return (
                <span
                  key={index}
                  className={`w-full md:w-fit text-center px-4 py-2 bg-white-100 border-gray-250 text-black-100 rounded-full cursor-pointer`}
                  style={{
                    backgroundColor: finalModSelections[wordIndex] ? primary : "",
                    borderWidth: finalModSelections[wordIndex] ? "" : "1px",
                    color: finalModSelections[wordIndex] ? "#ffffff" : ""
                  }}
                  dangerouslySetInnerHTML={{
                    __html: `<span class="flex gap-2">${word} (${wordScore})</span>`
                  }}
                  onClick={() => selectModWord(wordIndex)}
                />
              );
            })}
          </div>

          <div className={`flex flex-col gap-3 items-center justify-center my-4`}>
            {result.map((w, index) => {
              const [wordIndex] = w;
              const word = wordOptions[wordIndex];

              if (!finalModSelections[wordIndex]) return null;

              return (
                <span
                  key={index}
                  className="text-5xl font-bold text-black-200"
                  style={{
                    color: primary ? primary : ""
                  }}
                  dangerouslySetInnerHTML={{
                    __html: word
                  }}
                />
              );
            })}
          </div>
        </div>
      </div>
      <div className="overflow-y-auto mt-8 w-full">
        <Conference isLinear={true} />
      </div>
    </RemoteLayout>
  );
};

export default Task9b;
